/**
 * KbApiService — adapter entre los endpoints estilo MockService y la API REST real del Worker.
 *
 * Los controllers llaman ApiService.post("kb/articulos", data) etc.
 * Este servicio intercepta esas llamadas cuando VITE_USE_MOCK=false
 * y las convierte en GET/POST/PUT/DELETE correctos con Authorization: Bearer.
 *
 * El token se obtiene automáticamente de localStorage (currentUser.token).
 * Los controllers no necesitan pasar tokens ni cambiar ninguna llamada.
 */

import { AppConfig } from "../utils/AppConfig.js";

function getBaseUrl() {
  return AppConfig.api.baseUrl;
}

function getToken() {
  try {
    const user = JSON.parse(localStorage.getItem("currentUser") || "null");
    return user?.token || null;
  } catch {
    return null;
  }
}

function buildHeaders(tok) {
  const h = { "Content-Type": "application/json" };
  const t = tok || getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

function toQuery(params) {
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v != null && v !== "" && v !== false
  );
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

async function req(method, path, body = null) {
  const url = `${getBaseUrl()}/${path}`;
  const opts = { method, headers: buildHeaders() };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Error ${res.status}`);
  }
  return res.json();
}

/**
 * Sube un archivo a R2 via Worker. El Worker extrae su texto y crea el adjunto
 * (sin artículo; se asocia al guardar). Devuelve el adjunto:
 * { id, url, nombre, mime, tamano, origen, tieneTexto, texto, error }.
 */
export async function uploadFile(file) {
  const url = `${getBaseUrl()}/admin/upload`;
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const body = new FormData();
  body.append("file", file);

  const res = await fetch(url, { method: "POST", headers, body });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Error ${res.status}`);
  }
  return (await res.json()).data;
}

/**
 * Mapea un endpoint estilo MockService al llamado REST correcto.
 * Retorna una Promise si el endpoint es manejado, o undefined si no lo es
 * (para que ApiService.js lo deje pasar al makeRequest genérico).
 */
export function routeKbEndpoint(endpoint, data) {
  // ── Endpoints que no existen en el Worker — respuesta vacía inmediata ────────
  if (endpoint.includes("condicionesIVA")) return Promise.resolve({ status: 0, message: [] });

  // ── Auth ────────────────────────────────────────────────────────────────────
  // Canje del token de un proveedor externo (LCApii) por una sesión de KnowFlow
  if (endpoint === "auth/externo") {
    return req("POST", "auth/externo", data);
  }

  if (endpoint.includes("login")) {
    return req("POST", "auth/login", data);
  }

  if (endpoint === "auth/profile") {
    return req("PUT", "auth/profile", data);
  }

  if (endpoint === "auth/password") {
    return req("PUT", "auth/password", data);
  }

  if (endpoint === "auth/forgot") {
    return req("POST", "auth/forgot", data);
  }

  if (endpoint === "auth/reset") {
    return req("POST", "auth/reset", data);
  }

  // ── KB — Motor de respuestas (nombre del modelo para el chat) ────────────────
  if (endpoint === "kb/info") {
    return req("GET", "kb/info");
  }

  // ── Categorías ──────────────────────────────────────────────────────────────
  if (endpoint === "categorias") {
    return req("GET", "categorias");
  }

  // ── KB — Artículos ───────────────────────────────────────────────────────────
  if (endpoint.includes("kb/articulos")) {
    // categoriasPermitidas viene del JWT en el Worker — no se envía
    const q = toQuery({
      categoriaId: data?.categoriaId,
      subcategoriaId: data?.subcategoriaId,
      q: data?.q,
      tipo: data?.tipo,
      tag: data?.tag || undefined,
      ai: data?.ai || undefined,
      page: data?.page || undefined,
      pageSize: data?.pageSize || undefined,
    });
    return req("GET", `kb/articulos${q}`);
  }

  if (endpoint.includes("kb/articulo")) {
    return req("GET", `kb/articulo/${data?.id}`);
  }

  if (endpoint.includes("kb/votar")) {
    return req("POST", "kb/votar", { articuloId: data?.articuloId, tipo: data?.tipo });
  }

  if (endpoint.includes("kb/ask")) {
    return req("POST", "kb/ask", { question: data?.question, history: data?.history, categoriaId: data?.categoriaId || undefined });
  }

  if (endpoint.includes("kb/api-query")) {
    return req("POST", "kb/api-query", { categoriaId: data?.categoriaId, inputs: data?.inputs });
  }

  // ── Admin — Artículos ───────────────────────────────────────────────────────
  if (endpoint.includes("admin/articulo/mejorar")) {
    return req("POST", "admin/articulo/mejorar", data);
  }

  if (endpoint.includes("admin/yt-transcript")) {
    const videoId = data?.videoId ?? new URLSearchParams(endpoint.split("?")[1] ?? "").get("videoId");
    const q = toQuery({ videoId });
    return req("GET", `admin/yt-transcript${q}`);
  }

  if (endpoint.includes("admin/articulo/sugerirRelacionados")) {
    const q = toQuery({ includeOtherCats: data?.includeOtherCats });
    return req("GET", `admin/articulo/${data?.id}/relacionados${q}`);
  }

  if (endpoint.includes("admin/articulo/ver")) {
    return req("GET", `admin/articulo/${data?.id}`);
  }

  // ── Admin — Adjuntos ────────────────────────────────────────────────────────
  if (endpoint.includes("admin/adjunto/enlace")) {
    return req("POST", "admin/adjunto/enlace", data);
  }

  if (endpoint.includes("admin/adjunto/reprocesar")) {
    return req("POST", `admin/adjunto/${data?.id}/reprocesar`);
  }

  if (endpoint.includes("admin/adjunto/editar")) {
    return req("PUT", `admin/adjunto/${data?.id}`, data);
  }

  if (endpoint.includes("admin/adjunto/eliminar")) {
    return req("DELETE", `admin/adjunto/${data?.id}`);
  }

  if (endpoint.includes("admin/adjunto")) {
    return req("GET", `admin/adjunto/${data?.id}`);
  }

  if (endpoint.includes("admin/articulo/crear")) {
    return req("POST", "admin/articulo", data);
  }

  if (endpoint.includes("admin/articulo/editar")) {
    return req("PUT", `admin/articulo/${data?.id}`, data);
  }

  if (endpoint.includes("admin/articulo/eliminar")) {
    return req("DELETE", `admin/articulo/${data?.id}`);
  }

  if (endpoint.includes("admin/articulos")) {
    return req("GET", "admin/articulos");
  }

  // ── Admin — Subcategorías ───────────────────────────────────────────────────
  if (endpoint.includes("admin/subcategoria/crear")) {
    return req("POST", "admin/subcategoria", data);
  }

  if (endpoint.includes("admin/subcategoria/editar")) {
    return req("PUT", `admin/subcategoria/${data?.id}`, data);
  }

  if (endpoint.includes("admin/subcategoria/eliminar")) {
    return req("DELETE", `admin/subcategoria/${data?.id}`);
  }

  // ── Admin — Categorías ──────────────────────────────────────────────────────
  if (endpoint.includes("admin/categoria/crear")) {
    return req("POST", "admin/categoria", data);
  }

  if (endpoint.includes("admin/categoria/editar")) {
    return req("PUT", `admin/categoria/${data?.id}`, data);
  }

  if (endpoint.includes("admin/categoria/eliminar")) {
    return req("DELETE", `admin/categoria/${data?.id}`);
  }

  if (endpoint.includes("admin/categorias")) {
    return req("GET", "admin/categorias");
  }

  // ── Sugerencias ─────────────────────────────────────────────────────────────
  if (endpoint === "sugerencias") {
    return req("POST", "sugerencias", data);
  }

  if (endpoint.includes("admin/sugerencia/eliminar")) {
    return req("DELETE", `admin/sugerencia/${data?.id}`);
  }

  if (endpoint.includes("admin/sugerencias")) {
    return req("GET", "admin/sugerencias");
  }

  // ── Admin — Usuarios ────────────────────────────────────────────────────────
  if (endpoint.includes("admin/usuario/invite")) {
    return req("POST", `admin/usuario/${data?.id}/invite`);
  }

  if (endpoint.includes("admin/usuario/permisos")) {
    return req("PUT", `admin/usuario/${data?.id}/permisos`, data);
  }

  if (endpoint.includes("admin/usuario/crear")) {
    return req("POST", "admin/usuario", data);
  }

  if (endpoint.includes("admin/usuario/editar")) {
    return req("PUT", `admin/usuario/${data?.id}`, data);
  }

  if (endpoint.includes("admin/usuario/eliminar")) {
    return req("DELETE", `admin/usuario/${data?.id}`);
  }

  if (endpoint.includes("admin/usuarios")) {
    return req("GET", "admin/usuarios");
  }

  // No manejado — ApiService usará su makeRequest genérico
  return undefined;
}

// Datos de la respuesta del chat además del texto: fuentes, pregunta corregida y
// artículos sugeridos cuando no se encontró nada ("¿Quisiste decir?")
export function metaRespuesta(resp = {}) {
  return {
    fuentes: resp.fuentes || [],
    fuentesDetalle: resp.fuentesDetalle || [],
    correccion: resp.correccion || null,
    sugerencias: resp.sugerencias || [],
  };
}

/**
 * Streaming SSE para /kb/ask.
 * Llama onToken(text) por cada fragmento y onDone(metaRespuesta) al finalizar.
 * Retorna un AbortController por si se quiere cancelar.
 */
export function streamAsk(question, { onToken, onDone, onError, history, categoriaId }) {
  const abort = new AbortController();
  const url = `${getBaseUrl()}/kb/ask`;

  fetch(url, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ question, stream: true, history, categoriaId: categoriaId || undefined }),
    signal: abort.signal,
  }).then(async(res) => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Error ${res.status}`);
    }

    // Si el Worker no pudo iniciar el stream (embeddings/LLM caídos) responde JSON
    // { answer, fuentes } con 200 en lugar de SSE: mostrar ese mensaje de degradación
    if ((res.headers.get("Content-Type") || "").includes("application/json")) {
      const json = await res.json();
      if (json.answer) onToken(json.answer);
      onDone?.(metaRespuesta(json));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let meta = {};

    while (true) {
      const { done, value } = await reader.read();

      // Al cerrar el stream se procesa también la última línea aunque no termine en \n
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = done ? "" : lines.pop(); // keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data: ") || trimmed === "data: [DONE]") {
          if (trimmed === "data: [DONE]") {
            onDone?.(metaRespuesta(meta));
            return;
          }
          continue;
        }
        try {
          const parsed = JSON.parse(trimmed.slice(6));
          if (parsed.token) onToken(parsed.token);
          // El evento final trae fuentes, fuentesDetalle, correccion y sugerencias
          if (parsed.fuentes) meta = parsed;
          if (parsed.error) throw new Error(parsed.error);
        } catch (e) {
          if (e.message && !e.message.includes("JSON")) throw e;
        }
      }
      if (done) break;
    }

    // If stream ended without [DONE], still notify
    onDone?.(metaRespuesta(meta));
  }).catch((err) => {
    if (err.name === "AbortError") return;
    onError?.(err);
  });

  return abort;
}
